/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import Swiper from 'swiper';
import * as Modules from 'swiper/modules';
import {swiperOptions, defaultOptions} from '../options/swiper.options';

/**
 * Swiper directive
 */
export const swiperDirective = {
  /**
   * Swiper's pre-defined options container
   */
  options: swiperOptions,

  /**
   * Initialize plugin for the given element
   *
   * @param {HTMLElement} element Directive HTML element
   * @param {object}      options Directive options
   */
  init(element, options) {
    const modules = Object.values(Modules);

    for (const [moduleName, moduleOptions] of Object.entries(defaultOptions)) {
      if (options[moduleName] === true) {
        options[moduleName] = {...moduleOptions};
      }
    }

    if (typeof options.thumbs?.swiper === 'string') {
      let thumbSwiper = document.querySelector(options.thumbs.swiper);

      if (thumbSwiper && !thumbSwiper.matches('[amb-swiper], [data-amb-swiper]')) {
        thumbSwiper = thumbSwiper.querySelector('[amb-swiper], [data-amb-swiper]');

        if (thumbSwiper) {
          options.thumbs.swiper = thumbSwiper;
        }
      }
    }

    if (options.manipulate !== false) {
      this.manipulateChildren(element, options);
    }

    if (options.enabled !== false) {
      new Swiper(element, {modules, ...options});
    }
  },

  /**
   * Destroy plugin for the given element
   *
   * @param {HTMLElement} element Directive HTML element
   */
  destroy(element) {
    if (element.swiper instanceof Swiper) {
      element.swiper.destroy();
    }
  },

  /**
   * Verify and manipulate child elements.
   *
   * The Swiper plugin requires its own structure for slides.
   * Some additional elements are also required by certain modules.
   *
   * ```html
   * <!-- Slider main container -->
   * <div class="swiper" amb-swiper>
   *   <!-- Required slide wrapper -->
   *   <div class="swiper-wrapper">
   *     <!-- Slides -->
   *     <div class="swiper-slide">Slide 1</div>
   *     <div class="swiper-slide">Slide 2</div>
   *     <div class="swiper-slide">Slide 3</div>
   *   </div>
   *
   *   <!-- Pagination module -->
   *   <div class="swiper-pagination"></div>
   *
   *   <!-- Navigation module -->
   *   <div class="swiper-button-prev"></div>
   *   <div class="swiper-button-next"></div>
   *
   *   <!-- Scrollbar module -->
   *   <div class="swiper-scrollbar"></div>
   * </div>
   * ```
   *
   * This structure isn't compatible with our existing carousel plugin, Slick. This method allow
   * init the Swiper plugin using a very simple structure, like Slick.
   *
   * It'll then check and manipulate child elements using the given options to meet the structure
   * that Swiper requires, before init the plugin.
   *
   * ```html
   * <!-- Slider main container -->
   * <div amb-swiper>
   *   <div>Slide 1</div>
   *   <div>Slide 2</div>
   *   <div>Slide 3</div>
   * </div>
   * ```
   *
   * @private
   *
   * @param {HTMLElement} element Directive HTML element
   * @param {object}      options Directive options
   */
  manipulateChildren(element, options) {
    // Gets all available module elements
    const moduleElements = [
      options.navigation?.prevEl,
      options.navigation?.nextEl,
      options.pagination?.el,
      options.scrollbar?.el
    ].filter((selector) => typeof selector === 'string' && !!selector);
    const moduleElementsSelector = moduleElements.join(', ');

    // Add default class to plugin container.
    element.classList.add('swiper');

    // Don't have `.swiper-wrapper` element, create one and move all child elements to inside.
    if (!element.querySelector('.swiper-wrapper')) {
      const wrapper = document.createElement('div');
      const children = [...element.children];

      children.forEach((child) => {
        if (moduleElementsSelector && child.matches(moduleElementsSelector)) {
          // Child element match one of the module elements
          // Keep it at the root level and do nothing...
        } else if (child.classList.contains('swiper-slide')) {
          // If the child element is already a `.swiper-slide`,
          // Move it to the `.swiper-wrapper`
          wrapper.appendChild(child);
        } else {
          // Otherwise, create a `.swiper-slide` wrapper
          // And move the element to this slide.
          const slide = document.createElement('div');

          slide.classList.add('swiper-slide');
          slide.appendChild(child);
          wrapper.appendChild(slide);
        }
      });

      wrapper.classList.add('swiper-wrapper');
      element.appendChild(wrapper);
    }

    // Loop through the available module elements,
    // Check, and create if it doesn't exist.
    moduleElements
      .forEach((selector) => {
        if (!element.querySelector(selector)) {
          const newEl = document.createElement('div');

          newEl.classList.add(selector.substring(1));

          element.appendChild(newEl);
        }
      })
    ;

    if (options.thumbs) {
      const swiperComponent = element.closest('.component.common--carousel');
      let thumbSwiper = typeof options.thumbs === 'string' ? options.thumbs : options.thumbs?.swiper;

      if (typeof thumbSwiper === 'string') {
        thumbSwiper = document.querySelector(thumbSwiper);
      } else if (thumbSwiper instanceof Swiper) {
        thumbSwiper = thumbSwiper.el;
      }

      if (thumbSwiper instanceof HTMLElement) {
        const thumbsComponent = thumbSwiper.closest('.component.common--carousel');

        (swiperComponent || element).append(thumbsComponent || thumbSwiper);
      }
    }
  },
};
